import { buildPrompt } from '@/ai/utils/promptBuilder'
import { systemMessages } from '@/ai/prompts/common/systemMessages'
import { examples, getEventExample } from '@/ai/prompts/common/examples'
import { getCardTypeInstruction } from '@/ai/prompts/common/instructions'
import { createSystemMessage, createUserMessage } from '@/ai/utils/promptUtils'
import { buildWorkflowSpec, buildCardTypesMap } from '@/shared/utils/workflowSpec'
import { getJsonArrayOutputFormatting, getJsonOutputFormatting } from '@/ai/prompts/common/formatting'
import { wrapUserInput } from '@/ai/utils/helpers'

export const buildWorkflowCardsPrompt = ({ workflow, cardTypes, stream }) => {
  const messages = []

  const systemContent = buildPrompt([systemMessages.EVENT_MODELING_DDD_EXPERT])
  messages.push(createSystemMessage(systemContent))

  const promptSections = []
  const cardTypeInstructionsContent = []
  const cardTypeConsiderationContent = []

  cardTypes.forEach((cardType) => {
    if (cardType.generateWithAI) {
      cardTypeInstructionsContent.push(getCardTypeInstruction(cardType))
      cardTypeConsiderationContent.push(cardType.title)
    }
  })

  promptSections.push({
    title: 'Request',
    content: 'For each domain event, generate the following information:',
    spacingAfter: 0
  })

  promptSections.push({
    contentType: 'bullet',
    content: cardTypeInstructionsContent
  })

  promptSections.push({
    title: 'Workflow Description',
    flattenLines: false,
    content: wrapUserInput(workflow.aiDescription)
  })

  const cardTypesMap = buildCardTypesMap(cardTypes)

  promptSections.push({
    title: 'Current Workflow Specification (JSON format)',
    contentType: 'codeblock',
    language: 'json',
    content: buildWorkflowSpec(workflow, null, cardTypesMap) || '{}'
  })

  promptSections.push({
    title: 'Very Important Considerations',
    content: [
      `It is crucial to ensure that the output contains exactly ${workflow.domainEvents.length} JSON objects (one for each event). Each JSON object must carry the following information:`,
      ...cardTypeConsiderationContent
    ]
  })

  const formattingContent = `The resulting array must contain ${workflow.domainEvents.length} items. Description must be in human readable format, not in code format (Order Row, but not OrderRow).`

  const options = {
    shouldAddCards: true,
    shouldAddSchemas: false
  }

  const exampleData1 = {
    name: 'Order Placed',
    commandExample: examples.commandExampleCreateOrder
  }
  const exampleData2 = {
    name: 'Order Delivered',
    commandExample: examples.commandExampleDeliverOrder
  }

  const eventsExample = [
    getEventExample(exampleData1, cardTypes, options),
    getEventExample(exampleData2, cardTypes, options)
  ]

  const eventsObject = { events: eventsExample }

  promptSections.push({
    title: 'Formatting',
    flattenLines: false,
    content: stream
      ? getJsonArrayOutputFormatting(JSON.stringify(eventsExample), formattingContent)
      : getJsonOutputFormatting('events', JSON.stringify(eventsObject), formattingContent)
  })

  const userContent = buildPrompt(promptSections)
  messages.push(createUserMessage(userContent))

  return { messages }
}
